import React, { createContext, useContext, useState } from "react";
import { PillsProps } from "../Components/MainDashboard";

export type ColumnFilterOption = { label: string; value: string };

export type ColumnDef = {
  name: string;
  label: string;
  urlParam: string;
  getOptionValue?: (rawValue: any) => ColumnFilterOption;
  staticOptions?: ColumnFilterOption[];
};

export type ColumnFilterContextType = {
  anchorEl: HTMLElement | null;
  activeColumn: ColumnDef | null;
  searchText: string;
  setSearchText: (t: string) => void;
  openFilter: (e: React.MouseEvent<HTMLElement>, col: ColumnDef) => void;
  closeFilter: () => void;
  getFilterOptions: () => ColumnFilterOption[];
  applyFilter: (values: string[]) => void;
  currentValues: string[];
  isColumnFiltered: (urlParam: string) => boolean;
};

export const ColumnFilterContext = createContext<ColumnFilterContextType | null>(null);

export const useColumnFilterContext = () => {
  const ctx = useContext(ColumnFilterContext);
  if (!ctx) throw new Error("useColumnFilterContext must be used within ColumnFilterContext.Provider");
  return ctx;
};

const parseJsonArray = (value: any): string[] => {
  if (!value || value === "null" || value === "[]") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export function useColumnFilters(
  data: any[],
  pills: PillsProps,
  onApply: (urlParam: string, values: string[]) => void
): ColumnFilterContextType {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeColumn, setActiveColumn] = useState<ColumnDef | null>(null);
  const [searchText, setSearchText] = useState("");

  const openFilter = (e: React.MouseEvent<HTMLElement>, col: ColumnDef) => {
    setAnchorEl(e.currentTarget);
    setActiveColumn(col);
    setSearchText("");
  };

  const closeFilter = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const getFilterOptions = (): ColumnFilterOption[] => {
    if (!activeColumn) return [];
    if (activeColumn.staticOptions) return activeColumn.staticOptions;
    const seen = new Set<string>();
    const options: ColumnFilterOption[] = [];
    for (const row of data) {
      const rawValue = row[activeColumn.name];
      if (rawValue === null || rawValue === undefined) continue;
      const option = activeColumn.getOptionValue
        ? activeColumn.getOptionValue(rawValue)
        : { label: String(rawValue), value: String(rawValue) };
      if (option.label && !seen.has(option.value)) {
        seen.add(option.value);
        options.push(option);
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  };

  const currentValues = activeColumn
    ? activeColumn.urlParam === "visible"
      ? (() => {
          const v = String((pills as any)["visible"] ?? "null");
          return v !== "null" && v !== "" ? [v] : [];
        })()
      : parseJsonArray((pills as any)[activeColumn.urlParam])
    : [];

  const applyFilter = (values: string[]) => {
    if (!activeColumn) return;
    onApply(activeColumn.urlParam, values);
    closeFilter();
  };

  const isColumnFiltered = (urlParam: string): boolean =>
    parseJsonArray((pills as any)[urlParam]).length > 0;

  return {
    anchorEl,
    activeColumn,
    searchText,
    setSearchText,
    openFilter,
    closeFilter,
    getFilterOptions,
    applyFilter,
    currentValues,
    isColumnFiltered,
  };
}
